import React, { FunctionComponent, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import { noop } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { headerNavItemData } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'
import { HEADER_NAV_ITEM } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'

import { FavoritedStar } from '../../atoms/icons/FavoritedStar'
import { GroupAddIcon } from '../../atoms/icons/GroupAddIcon'
import { HeartDottedBorderIcon } from '../../atoms/icons/HeartDottedBorderIcon'
import { MenuIcon } from '../../atoms/icons/MenuIcon'
import { QuestionMarkIcon } from '../../atoms/icons/QuestionMarkIcon'
import { SessionIcon } from '../../atoms/icons/SessionIcon'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { Menu, MenuItemProps } from '../menu/Menu'

export interface HeaderNavMenuProps {
  handleBenefitsPress: () => void
  handleAccountPress: () => void
  handleFAQsPress?: () => void
  handleReferralPress?: () => void
  handleLogOutPress: () => void
  handleSessionsPress?: () => void
  handleFavoritedProvidersStarIconPress?: ({ isFromHamburgerMenu }: { isFromHamburgerMenu: boolean }) => void
  shouldShowFAQs?: () => boolean
  shouldShowReferral?: () => boolean
  shouldShowSessions?: () => boolean
  shouldShowFavoritedProvidersStarIcon?: () => boolean
  currentPathname?: string
  currentScreen?: string
  trackMenuClick?: () => void
  showAccountBadge?: boolean
  animateAccountBadge?: boolean
  highlightTrigger?: boolean
  pressableTriggerStyle?: (pressed: boolean, focusVisible: boolean) => ViewStyle
}

export interface HeaderNavMenuAndTabBarProps extends HeaderNavMenuProps {
  handleHomePress?: () => void
  handleCarePress?: () => void
  handleEssentialsPress?: () => void
  shouldShowHome?: () => boolean
  shouldShowCare?: () => boolean
  shouldShowEssentials?: () => boolean
}

interface HeaderNavMenuItemProps extends MenuItemProps {
  condition?: () => boolean
  activePathnames?: string[]
  activeScreens?: string[]
}

export const HeaderNavMenu: FunctionComponent<HeaderNavMenuProps> = ({
  handleBenefitsPress,
  handleAccountPress,
  handleFAQsPress = noop,
  handleReferralPress = noop,
  handleLogOutPress,
  handleSessionsPress = noop,
  handleFavoritedProvidersStarIconPress = noop,
  shouldShowFAQs = () => true,
  shouldShowReferral,
  shouldShowSessions,
  shouldShowFavoritedProvidersStarIcon,
  currentPathname,
  currentScreen,
  trackMenuClick,
  showAccountBadge = false,
  animateAccountBadge = true,
  highlightTrigger = false,
  pressableTriggerStyle,
}) => {
  const { formatMessage } = useIntl()
  const { colors, breakpoints } = useTheme() as ThemeType
  const [activeMenuItem, setActiveMenuItem] = useState<string | undefined>(undefined)
  const testId = 'HeaderNavMenu'
  const menuStyle = { marginTop: breakpoints.isMinWidthTablet ? 20 : 16 }

  const menuItemsList: HeaderNavMenuItemProps[] = [
    {
      id: HEADER_NAV_ITEM.SESSIONS,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.SESSIONS].label),
      onPressItem: handleSessionsPress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.SESSIONS].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.SESSIONS].activeScreenNames,
      condition: shouldShowSessions,
      icon: (isSelected) => (
        <SessionIcon isFilled={false} fillColor={isSelected ? colors.iconActive : colors.iconDefault} />
      ),
    },
    {
      id: HEADER_NAV_ITEM.BENEFITS,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.BENEFITS].label),
      onPressItem: handleBenefitsPress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.BENEFITS].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.BENEFITS].activeScreenNames,
      icon: (isSelected) => <HeartDottedBorderIcon fillColor={isSelected ? colors.iconActive : colors.iconDefault} />,
    },
    {
      id: HEADER_NAV_ITEM.ACCOUNT,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.ACCOUNT].label),
      onPressItem: handleAccountPress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.ACCOUNT].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.ACCOUNT].activeScreenNames,
      icon: (isSelected) => <MenuIcon fillColor={isSelected ? colors.iconActive : colors.iconDefault}></MenuIcon>,
      showBadge: IS_WEB ? showAccountBadge : undefined,
    },
    {
      id: HEADER_NAV_ITEM.FAQS,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.FAQS].label),
      condition: shouldShowFAQs,
      onPressItem: handleFAQsPress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.FAQS].activePathnames,
      icon: (isSelected) => (
        <QuestionMarkIcon width={24} fillColor={isSelected ? colors.iconActive : colors.iconDefault} />
      ),
    },
    {
      id: HEADER_NAV_ITEM.REFERRAL,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.REFERRAL].label),
      onPressItem: handleReferralPress,
      condition: shouldShowReferral,
      icon: (isSelected) => <GroupAddIcon fillColor={isSelected ? colors.iconActive : colors.iconDefault} />,
    },
    {
      id: HEADER_NAV_ITEM.FAVORITED_PROVIDERS,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.FAVORITED_PROVIDERS].label),
      onPressItem: () => handleFavoritedProvidersStarIconPress({ isFromHamburgerMenu: true }),
      condition: shouldShowFavoritedProvidersStarIcon,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.FAVORITED_PROVIDERS].activePathnames,
      icon: (isSelected) => <FavoritedStar fillColor={isSelected ? colors.iconActive : undefined} />,
    },
    {
      id: HEADER_NAV_ITEM.LOG_OUT,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.LOG_OUT].label),
      onPressItem: handleLogOutPress,
      itemStyle: { marginTop: 8, justifyContent: breakpoints.isMobileSized ? 'center' : 'flex-start' },
    },
  ]
  menuItemsList.forEach((item) => (item.testId = `${testId}-${item.id}`))
  const menuItemsListFiltered = menuItemsList.filter((item) => {
    return (item.condition && item.condition()) ?? true
  })

  useEffect(() => {
    const foundActivePath = menuItemsListFiltered.filter((item) => {
      if (currentPathname) {
        return (
          item.activePathnames &&
          item.activePathnames.some((activePath) => {
            return currentPathname.includes(activePath)
          })
        )
      }
      if (currentScreen) {
        return (
          item.activeScreens &&
          item.activeScreens.some((activePath) => {
            return currentScreen.includes(activePath)
          })
        )
      }
      return false
    })

    let activeItemText = ''
    if (foundActivePath.length > 0) {
      // Find longest match (eg being on /search-care/benefits should not match for the homepage)
      activeItemText = foundActivePath.reduce((max: HeaderNavMenuItemProps, item: HeaderNavMenuItemProps) => {
        return item.text.length > max.text.length ? item : max
      }, foundActivePath[0]).text
    }

    setActiveMenuItem(activeItemText)
  }, [currentPathname, currentScreen, menuItemsListFiltered])

  return (
    <Menu
      menuItems={menuItemsListFiltered}
      testID={testId}
      horizontalPosition='right'
      dropdownStyle={menuStyle}
      preselectedValue={activeMenuItem}
      showIcons
      trackMenuClick={trackMenuClick}
      showMenuBadge={showAccountBadge}
      animateBadge={animateAccountBadge}
      highlightTrigger={highlightTrigger}
      pressableTriggerStyle={pressableTriggerStyle}
      menuItemStyle={{ paddingVertical: 16, paddingHorizontal: 24 }}
      dropdownWidth={215}
    />
  )
}
